import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { Anchor } from "./Navbar";

const HERO_IMG =
    "https://images.unsplash.com/photo-1776627780164-865032b49cc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwZmFzaGlvbiUyMG1vZGVsJTIwc3BvdGxpZ2h0fGVufDB8fHx8MTc4Njg3MTgxOXww&ixlib=rb-4.1.0&q=85";

const EASE_OUT_EXPO = [0.22, 1, 0.36, 1];

const lineVariant = {
    hidden: { y: "110%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 1, ease: EASE_OUT_EXPO, delay: 0.2 + i * 0.14 },
    }),
};

const overlineAnim = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay: 0.1 },
};

const subheadlineAnim = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, delay: 0.75 },
};

const ctaAnim = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, delay: 0.9 },
};

const imageAnim = {
    initial: { opacity: 0, scale: 0.96 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 1.2, delay: 0.5, ease: EASE_OUT_EXPO },
};

const scrollHintAnim = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { delay: 1.6, duration: 1 },
};

const MaskedLine = ({ index, children, className = "" }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span className={`block ${className}`} custom={index} variants={lineVariant} initial="hidden" animate="show">
            {children}
        </motion.span>
    </span>
);

const HeroCopy = () => (
    <div className="lg:col-span-7 relative z-10">
        <motion.p
            {...overlineAnim}
            className="text-xs uppercase tracking-[0.3em] font-mono-accent text-amber-500/80 mb-8"
            data-testid="hero-overline"
        >
            LookAura — AI Fashion Intelligence
        </motion.p>

        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter leading-[0.95] text-[#FAFAFA]" data-testid="hero-headline">
            <MaskedLine index={0}>Your AI</MaskedLine>
            <MaskedLine index={1} className="italic text-amber-500">Appearance</MaskedLine>
            <MaskedLine index={2}>Coach.</MaskedLine>
        </h1>

        <motion.p
            {...subheadlineAnim}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-zinc-400"
            data-testid="hero-subheadline"
        >
            Discover your best look with AI-powered personalized appearance and fashion recommendations. LookAura analyzes your appearance and preferences — then guides you toward styles, looks, and products made for you.
        </motion.p>

        <motion.div {...ctaAnim} className="mt-10 flex flex-wrap items-center gap-4">
            <Anchor
                hash="#app"
                testId="hero-get-started-button"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-amber-500 text-[#050505] font-semibold text-sm hover:bg-amber-400 transition-colors duration-300"
            >
                Get Started
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Anchor>
            <Anchor
                hash="#how-it-works"
                testId="hero-how-it-works-button"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-amber-600/60 text-amber-400 font-semibold text-sm hover:bg-amber-500/10 transition-colors duration-300"
            >
                How It Works
            </Anchor>
        </motion.div>
    </div>
);

const HeroImage = ({ imgY }) => (
    <motion.div {...imageAnim} className="lg:col-span-5 relative">
        <div className="absolute -inset-8 gold-glow pointer-events-none" />
        <div className="relative overflow-hidden border border-white/10" data-testid="hero-image-frame">
            <motion.img
                src={HERO_IMG}
                alt="Fashion model under a dramatic spotlight, representing LookAura's premium AI styling experience"
                style={{ y: imgY }}
                className="w-full h-[26rem] sm:h-[32rem] lg:h-[36rem] object-cover scale-110"
                loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 via-transparent to-transparent" />
        </div>
        <p className="mt-4 text-[11px] uppercase tracking-[0.25em] font-mono-accent text-zinc-500">Analysis-driven style, not guesswork</p>
    </motion.div>
);

const ScrollHint = () => (
    <motion.div
        {...scrollHintAnim}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-zinc-500"
    >
        <span className="text-[10px] uppercase tracking-[0.3em] font-mono-accent">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
    </motion.div>
);

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const glowOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-[72px]">
            <motion.div style={{ opacity: glowOpacity }} className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-32 -left-32 w-[42rem] h-[42rem] gold-glow" />
                <div className="absolute bottom-0 right-0 w-[36rem] h-[36rem] gold-glow" />
            </motion.div>

            <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center py-24">
                <HeroCopy />
                <HeroImage imgY={imgY} />
            </div>

            <ScrollHint />
        </section>
    );
}
